import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/shared/auth/services/auth.service';
import { Perfil } from 'src/app/shared/enums/Perfis/Perfis';
import { Categoria } from 'src/app/shared/models/categoria/Categoria';
import { ListarChamados } from 'src/app/shared/models/chamados/listar-chamados';
import { Diretoria } from 'src/app/shared/models/diretoria/Diretoria';
import { Escola } from 'src/app/shared/models/escola/Escola';
import { Turno } from 'src/app/shared/models/escola/Turno';
import { AtribuicaoLoginSed, UserLoginSed } from 'src/app/shared/models/sed/UserLogin';
import { Turma } from 'src/app/shared/models/turma/Turma';
import { CategoriaService } from 'src/app/shared/services/categoria.service';
import { ChamadoService } from 'src/app/shared/services/chamado.service';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { EscolaService } from 'src/app/shared/services/escola.service';
import { PsicologoService } from 'src/app/shared/services/psicologo.service';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';
import { GenericValidator } from 'src/app/shared/validations/genericValidators';

@Component({
  selector: 'dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [angularCommonImports, convivaCommonImports, libsCommonImports]
})

export class DashboardComponent implements OnInit, OnDestroy {
  //Injeções
  private config = inject(NgSelectConfig);
  private escolaService = inject(EscolaService);
  private chamadoService = inject(ChamadoService);
  private spinnerService = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);
  private ConvivaErrors = inject(ConvivaErrorHandlingService);
  private authService = inject(AuthService);
  private categoriaService = inject(CategoriaService);
  private psicologoService = inject(PsicologoService);
  private validatorsService = inject(GenericValidator);
  private router = inject(Router);

  private get DataAtual() { return new Date() }
  public minDate: string = '2021-01-01';
  public maxDate: string = `${this.DataAtual.getFullYear()}-12-30`;

  // Variaveis de Login
  public userLogado: UserLoginSed | null;
  public atribuicoesLogin: AtribuicaoLoginSed;

  public colunasGrid = [{ title: 'Chamado', field: 'codigoChamado', width: 100, hozAlign: "center", headerHozAlign: "center" },
  { title: 'Data de Registro da Ocorrência', field: 'dataChamado', width: 100, hozAlign: "center", headerHozAlign: "center",
    formatter: (cell: any) => {const value = cell.getValue();
      if (!value) return '';

      const data = new Date(value);

      const dia = String(data.getDate()).padStart(2, '0');
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const ano = data.getFullYear();

      return `${dia}/${mes}/${ano}`;
    } },
  { title: 'Gravidade', field: 'nomeGravidade', width: 100, hozAlign: "center", headerHozAlign: "center" },
  { title: 'Categoria', field: 'nomeCategoria', width: 200, hozAlign: "center", headerHozAlign: "center"},
  { title: 'Subcategoria', field: 'nomeSubCategoria', width: 250, hozAlign: "center", headerHozAlign: "center" },
  { title: 'Status', field: 'dsStatus', width: 150, hozAlign: "center", headerHozAlign: "center" },
  { title: 'Escola', field: 'nomeEscola', width: 280, hozAlign: "center", headerHozAlign: "center" },
  {
    title: 'Ações', field: 'visualizar', width: 100, hozAlign: "center", formatter: function (cell: any, formatterParams: any, onRendered: any) {
      return `<div style='display:inline-flex;gap:0.4em;'> <i class='fa fa-eye'  aria-hidden='true'></i>`

        ;
    }, headerHozAlign: "center", cellClick: (e: any, cell: any) => this.OnClickAcao(e, cell)
  }
  ];

  constructor() {
    // ng-select mudança de texto
    this.config.notFoundText = 'Não encontrado';

    // Dados usuário logado
    this.userLogado = this.authService.getUser();
    if (this.userLogado != null && !this.userLogado.flPsicologo) {
      this.atribuicoesLogin = this.userLogado?.DadosUsuario[0];
    }

    this.atividadeDiretoria = this.userLogado?.apenasDiretoria || false;
  }

  public exibirEscola: boolean = false;
  public atividadeDiretoria: boolean = false;

  // Listas da tela
  public gravidades = [{ id: 1, name: 'Amarelo' }, { id: 2, name: 'Vermelho' }];
  public diretorias: Diretoria[] = [];
  public escolas: Escola[] = [];
  public turnos: Turno[] = [];
  public turmas: Turma[] = [];
  public lstChamados: ListarChamados[] = [];
  public categorias: Categoria[] = [];

  //Form
  public formDashboard!: FormGroup;
  public get form() { return this.formDashboard.controls; }
  public submitted: boolean;
  public showPesquisa: boolean;

  //Totalizadores
  public totalChamados: number = 0;
  public totalChamadosAbertos: number = 0;
  public totalChamadosEncerrados: number = 0;
  public totalChamadosEncaminhados: number = 0;
  public totalChamadosAgendados: number = 0;
  public totalChamadosAtendidos: number = 0;

  public async ngOnInit() {

    // Restaura escola vinculada se a sessão ficou com 0 (#16736)
    if (this.isGestorEscolar() && this.userLogado) {
      this.restaurarEscolaVinculadaNaSessao();
    }

    //Formulario de pesquisa.
    this.formDashboard = new FormGroup({
      codigoDiretoria: new FormControl(null, [Validators.required]),
      codigoEscola: new FormControl(null, this.isGestorEscolar() ? [Validators.required] : []),
      codigoTurno: new FormControl(null),
      codigoTurma: new FormControl(null),
      codigoGravidade: new FormControl(null),
      codigoSubCategoria: new FormControl(null),
      dataInicio: new FormControl(null),
      dataFim: new FormControl(null),
    });

    await this.getCategorias();

    if (this.userLogado?.flPsicologo) {
      await this.getDiretoriasPerfilPsicologo();
    } else {
      if (this.atribuicoesLogin.NumeroComportamento == 1) {
        await this.getDiretorias();
      }

      if (this.atribuicoesLogin.NumeroComportamento == 2) {
        this.getDiretoriasPerfilDiretoria();
      }

      if (this.atribuicoesLogin.NumeroComportamento == 3 || this.atribuicoesLogin.NumeroComportamento == 4) {
        await this.getDiretoriasPerfilEscola();
      }

    }



    this.formDashboard.get('codigoEscola')?.valueChanges.subscribe(changeEscolaValue => {
      changeEscolaValue ? this.form.codigoTurno.enable() : this.form.codigoTurno.disable()
    })
  }

  /** Diretor / Vice-Diretor: segregação por escola vinculada (#16736). */
  public isGestorEscolar(): boolean {
    const perfil = this.userLogado?.codigoPerfil;
    return perfil === Perfil.DiretorEscolar || perfil === Perfil.ViceDiretor;
  }

  public validaAno(data: AbstractControl<any, any>) {
    const dataDoCampo = data.value;
    if (dataDoCampo) {
      const ano = new Date(dataDoCampo).getFullYear();
      if (ano > this.DataAtual.getFullYear() || ano < this.DataAtual.getFullYear() - 10) {
        data.setValue(null);
        this.toastr.warning("Data invalida! Selecione uma data valida para pesquisar.");
      }
    }
  }

  public ngOnDestroy(): void {
    // this.dtTrigger.unsubscribe();
  }

  public async PesquisarChamados(statusEnvolvido: number = -1) {
    this.spinnerService.show();
    this.submitted = true;

    if (this.isGestorEscolar()) {
      const codigoEscolaRaw = this.formDashboard.getRawValue()?.codigoEscola
        ?? this.userLogado?.codigoEscola;
      if (!codigoEscolaRaw || codigoEscolaRaw <= 0) {
        this.travarEscolaGestor(this.form.codigoDiretoria.value);
      }
    }

    if (this.formDashboard.invalid) {
      this.showPesquisa = false;
      this.spinnerService.hide();
      return;
    }
    this.showPesquisa = true;
    const parametros = this.SetParametrosChamados(statusEnvolvido);

    if (this.isGestorEscolar() && (!parametros.codigoEscola || parametros.codigoEscola <= 0)) {
      this.toastr.warning("Escola é obrigatória para este perfil.");
      this.showPesquisa = false;
      this.spinnerService.hide();
      return;
    }

    const response = await lastValueFrom(this.chamadoService.getDashboard(parametros))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });
    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }
    this.spinnerService.hide();
    this.lstChamados = response.data;
    if (this.lstChamados.length == 0) {
      this.SetTotalizadores();
      this.toastr.warning("Não existem chamados para essa pesquisa");
      this.lstChamados = [];

    }
    else {
      if (parametros.codigoDiretoria != null && parametros.codigoEscola == null && parametros.codigoCategoria == null && parametros.codigoTurno == null) {
        this.exibirEscola = true;
        //  this.isDtInitialized = true;
        //  this.dtTrigger.next(this.dtOptions);
      }
      this.SetTotalizadores();
      // if (this.isDtInitialized) {
      //   this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      //     dtInstance.destroy();
      //     this.dtTrigger.next(this.dtOptions);
      //   });
      // } else {
      //   this.isDtInitialized = true;
      //   if(this.dtElement.dtInstance != null) {
      //     this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      //       dtInstance.destroy();
      //     });
      //   }
      //   this.dtTrigger.next(this.dtOptions);
      // }
    }
  }

  public SetParametrosChamados(statusEnvolvido: number): ListarChamados {
    const chamado = new ListarChamados();
    chamado.codigoDiretoria = this.form?.codigoDiretoria.value;
    // getRawValue: inclui escola mesmo quando o controle está disabled (gestor escolar)
    chamado.codigoEscola = this.formDashboard.getRawValue()?.codigoEscola
      ?? this.form?.codigoEscola.value;
    chamado.codigoTurno = this.form?.codigoTurno.value;
    chamado.codigoTurma = this.form?.codigoTurma.value;
    chamado.codigoGravidade = this.form?.codigoGravidade.value;
    chamado.codigoSubCategoria = this.form?.codigoSubCategoria.value;
    chamado.dataInicio = this.form?.dataInicio.value;
    if (this.form?.dataFim.value != null) {
      const date = new Date(this.form?.dataFim.value);
      date.setUTCHours(23, 59, 59, 0);
      chamado.dataFim = date;
    }
    chamado.filtroStatusEnvolvido = statusEnvolvido;
    //[128649] - removido a filtro de atividade diretoria do dashboard geral
    chamado.atividadeDiretoria = false;

    return chamado;
  }

  public async getDiretorias() {
    this.spinnerService.show();

    const response = await lastValueFrom(this.escolaService.getDiretoriaPorTipoDiretoria())
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });
    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }
    this.diretorias = response.data;
    this.spinnerService.hide();
    if (this.userLogado?.codigoDiretoria != undefined) {
      this.ativaDiretoria();
      this.getEscolas(true);
    }
  }

  public async getEscolas(diretoriaLogada: boolean = false) {

    const codigoDE = this.userLogado?.codigoDiretoria != undefined && diretoriaLogada ?
      this.userLogado?.codigoDiretoria :
      this.formDashboard.get('codigoDiretoria')?.value;

    if (this.userLogado?.flPsicologo) {
      this.zerarEscola();
      this.spinnerService.show();

      var response = await lastValueFrom(this.escolaService.getEscolasPorCodigoDiretoria(codigoDE))
        .catch((err) => {
          return this.ConvivaErrors.handleResultError(err);
        });
      if (!response.isSucess) {
        this.toastr.error(response.message);
        this.spinnerService.hide();
        return;
      }
      this.escolas = response.data;
      this.formDashboard.get('codigoEscola')?.enable();
      this.spinnerService.hide();
      this.ativaEscola();
    } else
      if (this.atribuicoesLogin.NumeroComportamento == 1 || this.atribuicoesLogin.NumeroComportamento == 2) {
        //[128649] - removido a filtro de atividade diretoria do dashboard geral
        //this.zerarEscola();
        if (codigoDE != null) {
          this.spinnerService.show();

          var response = await lastValueFrom(this.escolaService.getEscolasPorCodigoDiretoria(codigoDE))
            .catch((err) => {
              return this.ConvivaErrors.handleResultError(err);
            });
          if (!response.isSucess) {
            this.toastr.error(response.message);
            this.spinnerService.hide();
            return;
          }
          this.escolas = response.data;
          this.spinnerService.hide();
          this.ativaEscola();
        }
      } else if (this.atribuicoesLogin.NumeroComportamento == 3 || this.atribuicoesLogin.NumeroComportamento == 4) {
        this.zerarEscola();
        if (codigoDE != null) {
          this.spinnerService.show();
          this.userLogado?.DadosUsuario.filter(x => x.CodigoDiretoria == codigoDE).forEach(item =>
            this.escolas.push({
              codigoEscola: item.CodigoEscola,
              codigoDiretoria: item.CodigoDiretoria,
              nomeEscola: item.NomeEscola
            } as Escola)
          )
          this.form.codigoEscola.enable();
          this.spinnerService.hide();
          this.ativaEscola();
          if (this.isGestorEscolar() && this.escolas.length > 0) {
            this.travarEscolaGestor(codigoDE);
          }
        }
      }

    if (this.userLogado !== null) {
      this.userLogado.codigoDiretoria = codigoDE == null ? 0 : codigoDE;
      this.authService.setUser(this.userLogado);
    }
  }

  public zerarEscola() {
    this.escolas = [];
    this.formDashboard.get('codigoEscola')?.patchValue(null);
    this.form.codigoEscola.disable();
    this.zerarTurnos();
  }

  public async getTurnos() {

    const codigoEE = this.formDashboard.get('codigoEscola');
    if (codigoEE?.value != null) {
      this.spinnerService.show();
      const response = await lastValueFrom(this.escolaService.getTurnosPorCodigoCie(codigoEE?.value))
        .catch((err) => {
          return this.ConvivaErrors.handleResultError(err);
        });
      if (!response.isSucess) {
        this.toastr.error(response.message);
        this.spinnerService.hide();
        return;
      }
      this.turnos = response.data;
      if (this.turnos == null)
        this.toastr.warning('Não foi encontrados turnos para essa escola !');
      this.spinnerService.hide();
    }
    else {
      this.zerarTurnos();
    }
    if (this.userLogado !== null) {
      if (codigoEE?.value != null) {
        this.userLogado.codigoEscola = codigoEE.value;
        this.authService.setEscola(codigoEE.value);
      } else if (!this.isGestorEscolar()) {
        this.userLogado.codigoEscola = 0;
      }
      this.authService.setUser(this.userLogado);
    }
  }

  public zerarTurnos() {
    this.turnos = [];
    this.formDashboard.get('codigoTurno')?.patchValue(null);
    // this.form.codigoSubCategoria.disable();
  }

  public async getTurmas() {
    this.spinnerService.show();
    const codigoEs = this.formDashboard.get('codigoEscola');
    const codigoTurno = this.formDashboard.get('codigoTurno');

    if (codigoEs?.value != null) {
      const response = await lastValueFrom(this.escolaService.getTurmasPorCodigoCie(codigoEs?.value, codigoTurno?.value))
        .catch((err) => {
          return this.ConvivaErrors.handleResultError(err);
        });
      if (!response.isSucess) {
        this.toastr.error(response.message);
        this.spinnerService.hide();
        return;
      }
      this.turmas = response.data;
      this.spinnerService.hide();
    }
  }

  public zerarTurma() {
    this.turmas = [];
    this.formDashboard.get('codigoTurma')?.patchValue(null);
  }

  public getDiretoriasPerfilDiretoria() {
    this.diretorias = [];
    this.escolas = [];

    this.userLogado?.DadosUsuario?.map((item: AtribuicaoLoginSed) => {
      const diretoria = new Diretoria();
      diretoria.codigoDiretoria = item.CodigoDiretoria;
      diretoria.nomeDiretoria = item.NomeDiretoria;
      this.diretorias.push(diretoria);
    });

    if (this.diretorias.length == 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();
      this.getEscolas(true);
    } else {
      this.ativaDiretoria();
      this.getEscolas(true);
    }
  }

  public async getDiretoriasPerfilPsicologo() {
    this.diretorias = [];
    this.escolas = [];

    this.spinnerService.show();

    const response = await lastValueFrom(this.psicologoService.getDiretoriaPorPolo(this.userLogado!.codigoPolo))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }

    this.diretorias = response.data;
    this.spinnerService.hide();

    if (this.diretorias.length == 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();
      this.getEscolas(true);
    } else {
      this.ativaDiretoria();
      this.getEscolas(true);
    }
  }

  public async getDiretoriasPerfilEscola() {

    this.diretorias = [];
    this.escolas = [];

    this.userLogado?.DadosUsuario?.map((item: AtribuicaoLoginSed) => {
      const diretoria = new Diretoria();
      diretoria.codigoDiretoria = item.CodigoDiretoria;
      diretoria.nomeDiretoria = item.NomeDiretoria;
      this.diretorias.push(diretoria);
    });

    if (this.diretorias.length == 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();
    }

    if (this.diretorias.length == 1 && this.escolas.length > 1) {
      this.form.codigoDiretoria.setValue(this.diretorias[0].codigoDiretoria);
      this.form.codigoDiretoria.disable();
    }
    await this.ativaDiretoria();
  }

  public async ativaDiretoria() {
    if (this.userLogado?.codigoDiretoria != undefined) {
      this.formDashboard.get('codigoDiretoria')?.setValue(this.userLogado?.codigoDiretoria);
      await this.getEscolas();
    }
    if (this.userLogado !== null)
      this.authService.setUser(this.userLogado);
  }

  public ativaEscola() {
    if (this.isGestorEscolar()) {
      this.restaurarEscolaVinculadaNaSessao();
    }

    const codigoEscolaSessao = this.userLogado?.codigoEscola;
    if (codigoEscolaSessao != undefined && codigoEscolaSessao > 0) {
      const existEscola = this.escolas.find(s => s.codigoEscola == codigoEscolaSessao);
      if (existEscola != undefined) {
        this.formDashboard.get('codigoEscola')?.enable();
        this.formDashboard.get('codigoEscola')?.setValue(codigoEscolaSessao);
        if (this.isGestorEscolar()) {
          this.formDashboard.get('codigoEscola')?.disable();
        }
      }
    } else if (this.isGestorEscolar() && this.escolas.length > 0) {
      this.travarEscolaGestor(this.form.codigoDiretoria.value);
    }

    const codigoEscolaForm = this.formDashboard.get('codigoEscola')?.value
      ?? this.userLogado?.codigoEscola;
    if (this.userLogado?.codigoDiretoria != undefined
      && codigoEscolaForm != undefined
      && codigoEscolaForm > 0) {
      this.getTurnos();
      this.PesquisarChamados();
    }
    if (this.userLogado !== null)
      this.authService.setUser(this.userLogado);
  }

  private travarEscolaGestor(codigoDiretoria?: number | null): void {
    const codigoEscola = this.obterCodigoEscolaVinculada(codigoDiretoria)
      || this.escolas[0]?.codigoEscola;
    if (!codigoEscola) {
      return;
    }

    this.formDashboard.get('codigoEscola')?.setValue(codigoEscola);
    this.formDashboard.get('codigoEscola')?.disable();
    if (this.userLogado) {
      this.userLogado.codigoEscola = codigoEscola;
      this.userLogado.apenasDiretoria = false;
      this.authService.setUser(this.userLogado);
      this.authService.setEscola(codigoEscola);
    }
  }

  private obterCodigoEscolaVinculada(codigoDiretoria?: number | null): number {
    if (!this.userLogado) {
      return 0;
    }

    if (this.userLogado.codigoEscola && this.userLogado.codigoEscola > 0) {
      const existeNaLista = !this.escolas?.length
        || this.escolas.some(e => e.codigoEscola === this.userLogado!.codigoEscola);
      if (existeNaLista) {
        return this.userLogado.codigoEscola;
      }
    }

    const atribuicoes = this.userLogado.DadosUsuario ?? [];
    const filtradas = codigoDiretoria
      ? atribuicoes.filter(a => a.CodigoDiretoria === codigoDiretoria)
      : atribuicoes;
    const comEscola = filtradas.find(a => a.CodigoEscola > 0);
    return comEscola?.CodigoEscola ?? 0;
  }

  private restaurarEscolaVinculadaNaSessao(): void {
    if (!this.userLogado) {
      return;
    }

    if (this.userLogado.codigoEscola && this.userLogado.codigoEscola > 0) {
      this.userLogado.apenasDiretoria = false;
      this.authService.setUser(this.userLogado);
      this.authService.setEscola(this.userLogado.codigoEscola);
      return;
    }

    const codigoDiretoria = (this.userLogado.codigoDiretoria ?? 0) > 0
      ? this.userLogado.codigoDiretoria
      : this.userLogado.DadosUsuario?.[0]?.CodigoDiretoria;
    const codigoEscola = this.obterCodigoEscolaVinculada(codigoDiretoria);

    if (codigoEscola > 0) {
      this.userLogado.codigoEscola = codigoEscola;
      if (codigoDiretoria) {
        this.userLogado.codigoDiretoria = codigoDiretoria;
      }
      this.userLogado.apenasDiretoria = false;
      this.authService.setUser(this.userLogado);
      this.authService.setEscola(codigoEscola);
    }
  }

  public async getCategorias() {
    this.spinnerService.show();

    const response = await lastValueFrom(this.categoriaService.getCategoriasWithSub(0))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });
    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }
    this.categorias = response.data;
    this.spinnerService.hide();
  }

  private SetTotalizadores() {
    // if (!this.isDtInitialized) {
    if (this.lstChamados.length > 0) {
      this.totalChamados = this.lstChamados.length;
      this.totalChamadosAbertos = this.lstChamados.filter(r => r.status == 0).length;
      this.totalChamadosEncerrados = this.lstChamados.filter(r => r.status == 4).length;
      this.totalChamadosEncaminhados = this.lstChamados.filter(r => r.status == 1).length;
      this.totalChamadosAgendados = this.lstChamados.filter(r => r.status == 2).length;
      this.totalChamadosAtendidos = this.lstChamados.filter(r => r.status == 3).length;
    }
    else {
      this.totalChamados = 0;
      this.totalChamadosAbertos = 0;
      this.totalChamadosEncerrados = 0;
      this.totalChamadosEncaminhados = 0;
      this.totalChamadosAgendados = 0;
      this.totalChamadosAtendidos = 0;
    }
    // }
  }

  private OnClickAcao(e: any, cell: any) {
    // Evento de click na célula para capturar os botões
    const row = cell.getRow().getData(); // Dados da linha
    const target = e.target; // Elemento clicado
    const codigoChamado = row.codigoChamado

    if (target.classList.contains('fa-eye')) {
      this.router.navigate(['/detalhe/' + codigoChamado]);
    } else if (target.classList.contains('fa-pen')) {
      this.router.navigate(['/editar/' + codigoChamado]);
    } else if (target.classList.contains('fa-trash')) {
      this.router.navigate(['/remover/' + codigoChamado]);
    }
  }

  public OnChangeApenasDiretoria() {
    this.atividadeDiretoria = !this.atividadeDiretoria;
    this.userLogado!.apenasDiretoria = this.atividadeDiretoria;
    this.authService.setUser(this.userLogado!);
  }



}

